import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Caption from '@polestar/component-warehouse-react/components/caption/Caption';
import FormField, { getErrorText, hasErrorValue, } from '@polestar/component-warehouse-react/components/form-field/FormField';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { useFocusTriggers } from '@polestar/component-warehouse-react/hooks/useFocusTriggers';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import { focusProviderSkipProps } from '@polestar/component-warehouse-react/contexts/focus';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
const TextareaField = forwardRef(({ maxLength, value, indicatorFormat, onChange, onBlur, onFocus, name, minimumInputHeight = 84, maximumInputHeight, label, hint, error, errorMessage, trailing, tooltip, fieldProps, ...props }, ref) => {
    const [remainingCharacters, setRemainingCharacters] = useState(0);
    const [feedbackMessage, setFeedbackMessage] = useState(undefined);
    const hasError = hasErrorValue(error, errorMessage);
    const errorText = getErrorText(error, errorMessage);
    const showText = hasError && errorText !== undefined;
    const internalRef = useRef(null);
    const combinedRef = useCombinedRef(ref, internalRef);
    // Generate new id
    const memoizedId = useSSRSafeId('textarea-field', props.id);
    const heightTimeout = useRef(null);
    const checkHeight = useCallback(() => {
        if (heightTimeout.current) {
            clearTimeout(heightTimeout.current);
        }
        const heightTimeoutValue = 25;
        heightTimeout.current = setTimeout(() => {
            if (internalRef.current) {
                internalRef.current.style.height = '';
                internalRef.current.style.height = `${internalRef.current.scrollHeight + internalRef.current.scrollTop}px`;
            }
        }, heightTimeoutValue);
    }, []);
    const handleChange = useCallback((e) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(e.target.value, name);
        checkHeight();
    }, [onChange, name, checkHeight]);
    const { focused, focusBlurEvents } = useFocusTriggers({ name, onFocus, onBlur });
    /**
     * Keep track of the remaining characters
     */
    useEffect(() => {
        if (maxLength) {
            const remaining = maxLength - value.length;
            setRemainingCharacters(remaining);
        }
    }, [value, maxLength]);
    /**
     *
     */
    useEffect(() => {
        const message = typeof indicatorFormat === 'function'
            ? indicatorFormat(remainingCharacters)
            : `${remainingCharacters}`;
        setFeedbackMessage(maxLength ? message : undefined);
        checkHeight();
    }, [remainingCharacters, maxLength, indicatorFormat, checkHeight]);
    const FeedbackText = remainingCharacters < 0 || !!errorMessage ? ErrorLengthFeedback : LengthFeedback;
    return (React.createElement(FormField, { ...fieldProps, label: label, hint: React.createElement(HintWithFeedback, null,
            React.createElement(Hint, null, hint),
            React.createElement(Spacer, { as: "span", spacing: Spacings.medium, orientation: Orientations.horizontal }),
            React.createElement(FeedbackText, null, feedbackMessage)), error: showText ? (React.createElement(HintWithFeedback, null,
            React.createElement(Hint, null, errorMessage || (typeof error === 'string' && error)),
            React.createElement(Spacer, { as: "span", spacing: Spacings.medium, orientation: Orientations.horizontal }),
            React.createElement(FeedbackText, null, feedbackMessage))) : hasError && errorText === undefined ? (true) : undefined, trailing: trailing, forId: memoizedId, focused: focused, disabled: props.disabled, required: props.required, tooltip: tooltip },
        React.createElement(TextArea, { ...aria({
                multiline: true,
                disabled: props.disabled,
                live: 'polite',
                labelledby: !!label ? `${memoizedId}-label` : undefined,
                describedby: !!hint || !!error ? `${memoizedId}-message` : undefined,
                invalid: !!errorMessage || !!error,
            }), name: name, ...props, ...focusBlurEvents, ...focusProviderSkipProps, id: memoizedId, ref: combinedRef, value: value, onChange: handleChange, style: {
                maxHeight: maximumInputHeight,
                minHeight: minimumInputHeight,
            } })));
});
TextareaField.displayName = 'TextareaField';
export default TextareaField;
const TextArea = styled.textarea(({ theme, disabled }) => css(css `
      appearance: none;
      border-radius: 0;
      color: ${theme.textAreaField.states.enabled.color};
      border: none;
      width: 100%;
      min-height: ${Spacings.xxxxLarge};
      resize: none;
      background: none;
      flex-grow: 1;
      padding: ${createCustomSpacing(15 / 16)} ${Spacings.medium};
      box-sizing: border-box;
      display: flex;

      /* Hides the blue autocomplete styling from chrome */
      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus {
        appearance: none;
        transition: background-color 9999s ease-in-out 0s;
        -webkit-box-shadow: 0 0 0 1000px #fff inset;
      }

      &:focus,
      &:required,
      &:invalid {
        box-shadow: none;
        outline: none;
      }

      &::placeholder {
        color: ${theme.formField.states.enabled.placeholderColor};
      }
    `, disabled &&
    css `
        color: ${theme.textAreaField.states.disabled.color};
      `));
const HintWithFeedback = styled.span `
  display: flex;
  flex: 1;
`;
const Hint = styled.span `
  flex: 1;
`;
const LengthFeedback = styled(Caption) `
  white-space: nowrap;
  color: inherit;
`;
const ErrorLengthFeedback = styled(Caption)(({ theme }) => css `
    white-space: nowrap;
    color: ${theme.textAreaField.states.error.color};
  `);
